import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  Box,
  Skeleton,
  Typography,
  Divider,
  IconButton,
  Tooltip,
  Paper,
  ClickAwayListener,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
} from '@mui/material';
import {
  NumpadIcon,
  WaveformIcon,
  UserSoundIcon,
  BookIcon,
  CaretDownIcon,
  PhoneIcon,
  ChartLineIcon,
  StorefrontIcon,
  CoinIcon,
  TrademarkIcon,
  WhatsappLogoIcon,
  MegaphoneIcon,
  PencilSimpleIcon,
  UploadIcon,
  CaretLeftIcon,
  CaretRightIcon,
  Buildings,
  Plug,
} from '@phosphor-icons/react';
import { useDashboard } from '../../hooks/useDashboard';
import { useAgents } from '../../hooks/useAgents';
import { Agent } from '../../Providers/DashboardProvider';
import { useNavigate, useLocation } from 'react-router';
import { useAppDispatch, useAppSelector } from '../../hooks/useReduxTypeScript';
import {
  updateBranding,
  BrandingConfig,
} from '../../utils/redux/slices/brandingSlice';

import logo from '../../assets/images/favicon-dark.png';
import theme from '../../theme';
import UserProfileOptionMenu from './UserProfileOptionMenu';
import AgentAndBusinessButton from '../dashboard/AgentAndBusinessButton';
import { routes } from '../../constants/routes';
import { useUser } from '../../hooks/useUser';
import { useBrandingLogo } from '../../hooks/useBrandingLogo';
import { useAlert } from '../../hooks/useAlert';
import CircularProgress from '@mui/material/CircularProgress';
import { getFirstAvailableRoute } from '../../utils/getFirstAvailableRoute';

const expandedDrawerWidth = 270;
const collapsedDrawerWidth = 64;

interface DashboardSidebarProps {
  onWidthChange?: (width: number) => void;
  collapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
}

// Add overlay menu state interface
interface OverlayMenuState {
  isOpen: boolean;
  item: any;
  position: { top: number; left: number };
}

const DashboardSidebar: React.FC<DashboardSidebarProps> = ({
  onWidthChange,
  collapsed: collapsedProp,
  onCollapsedChange,
}) => {
  const { setSelectedAssistant, selectedAssistant, setSelectedAssistantId } =
    useDashboard();
  const { agents, isLoading } = useAgents();
  const { user } = useUser();
  const {
    uploadLogoAsync,
    isUploadingLogo,
    updateLogoMetadataAsync,
    isUpdatingMetadata,
    replaceLogoFileAsync,
    isReplacingLogo,
    deleteLogoAsync,
    isDeletingLogo,
  } = useBrandingLogo();
  const { showAlert } = useAlert();

  const navigate = useNavigate();
  const location = useLocation();
  const sidebarRef = useRef<HTMLDivElement>(null);
  const isScrollRestored = useRef(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Redux state for branding
  const dispatch = useAppDispatch();
  const brandingConfig = useAppSelector(state => state.branding.config);

  // Local state for edit dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [tempBranding, setTempBranding] =
    useState<BrandingConfig>(brandingConfig);

  // Collapsed state management with localStorage persistence
  const [internalCollapsed, setInternalCollapsed] = useState<boolean>(() => {
    const savedCollapsed = localStorage.getItem('sidebarCollapsed');
    return savedCollapsed ? JSON.parse(savedCollapsed) : false;
  });

  // Use external collapsed prop if provided, otherwise use internal state
  const isCollapsed =
    collapsedProp !== undefined ? collapsedProp : internalCollapsed;

  const [selectedMenus, setSelectedMenus] = useState<Set<string>>(() => {
    const savedMenus = localStorage.getItem('selectedMenus');
    return savedMenus ? new Set(JSON.parse(savedMenus)) : new Set();
  });

  // Add overlay menu state
  const [overlayMenu, setOverlayMenu] = useState<OverlayMenuState>({
    isOpen: false,
    item: null,
    position: { top: 0, left: 0 },
  });

  // Persist collapsed state and notify parent of width changes
  useEffect(() => {
    // Only persist to localStorage if using internal state
    if (collapsedProp === undefined) {
      localStorage.setItem(
        'sidebarCollapsed',
        JSON.stringify(internalCollapsed)
      );
    }

    if (onWidthChange) {
      onWidthChange(isCollapsed ? collapsedDrawerWidth : expandedDrawerWidth);
    }
  }, [isCollapsed, internalCollapsed, collapsedProp, onWidthChange]);

  const currentDrawerWidth = isCollapsed
    ? collapsedDrawerWidth
    : expandedDrawerWidth;

  useEffect(() => {
    localStorage.setItem('selectedMenus', JSON.stringify([...selectedMenus]));
  }, [selectedMenus]);

  useEffect(() => {
    const sidebar = sidebarRef.current;
    if (!sidebar) return;

    const handleScroll = () => {
      localStorage.setItem('sidebarScrollPosition', String(sidebar.scrollTop));
    };

    sidebar.addEventListener('scroll', handleScroll);
    return () => sidebar.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (sidebarRef.current && !isScrollRestored.current) {
      const savedScrollPosition = localStorage.getItem('sidebarScrollPosition');
      if (savedScrollPosition) {
        sidebarRef.current.scrollTop = Number(savedScrollPosition);
        isScrollRestored.current = true;
      }
    }
  }, []);

  const menuItems = useMemo(
    () => [
      {
        category: 'MANAGEMENT',
        items: [
          // {
          //   title: 'Order Management',
          //   icon: <CookieIcon />,
          //   path: '/order-management',
          // },
          // {
          //   title: 'Reservation Management',
          //   icon: <ChairIcon />,
          //   path: '/reservation-management',
          // },
          {
            title: 'Brand Management',
            icon: <TrademarkIcon />,
            path: '/brand-management',
          },
          {
            title: 'Outlet Management',
            icon: <StorefrontIcon />,
            path: '/store-management',
          },
          {
            title:
              user?.organization?.role === 'member'
                ? 'Organization'
                : 'Manage organization',
            icon: <Buildings />,
            path: routes.manageUsers,
          },
          // {
          //   title: 'Customer Management',
          //   icon: <UsersIcon />,
          //   path: '/customer-management',
          // },
        ],
      },
      {
        category: 'AI AGENT',
        items: [
          {
            title: 'Analytics',
            icon: <ChartLineIcon />,
            subItems: [
              {
                title: 'Agent-Analytics',
                path: `/analytics/${routes.agentAnalytics}`,
              },
              {
                title: 'Call-Evaluation',
                path: `/analytics/${routes.callEvaluation}`,
              },
              {
                title: 'Call-Auditing',
                path: `/analytics/${routes.callAuditing}`,
              },
            ],
          },
          {
            title: 'Agents',
            icon: <UserSoundIcon />,
            path: routes.agents,
          },
          {
            title: 'Knowledge Base',
            icon: <BookIcon />,
            subItems: [
              {
                title: 'Restaurant-Menu',
                path: '/knowledge-base/restaurant-menu',
              },
              {
                title: 'General-Support',
                path: '/knowledge-base/general-support',
              },
              {
                title: 'Reservation-Slots',
                path: '/knowledge-base/reservation',
              },
            ],
          },
          {
            title: 'Voice Library',
            icon: <WaveformIcon />,
            path: '/voice-library',
          },
          {
            title: 'Phone Numbers',
            icon: <NumpadIcon />,
            path: '/phone-numbers',
          },
          {
            title: 'Call Logs',
            icon: <PhoneIcon />,
            path: '/calls',
          },
          {
            title: 'WhatsApp Chats',
            icon: <WhatsappLogoIcon />,
            path: '/whatsapp-chats',
          },
          {
            title: 'Campaign',
            icon: <MegaphoneIcon />,
            path: routes.campaign,
          },
        ],
      },
      {
        category: 'BUILD',
        items: [
          {
            title: 'Integration',
            icon: <Plug />,
            path: routes.integration,
          },
        ],
      },
      {
        category: 'BILLING',
        items: [
          {
            title: 'Billing',
            icon: <CoinIcon />,
            path: '/billing',
          },
        ],
      },
    ],
    [user?.organization?.role]
  );

  // Filter menu items based on hideSections configuration
  const filteredMenuItems = useMemo(() => {
    if (!user?.hideSections) return menuItems;

    return menuItems
      .map(category => ({
        ...category,
        items: category.items.filter(item => {
          // Map menu item titles to hideSections keys
          const sectionKeyMap: Record<string, string> = {
            'Order Management': 'order_management',
            'Reservation Management': 'reservation_management',
            'Outlet Management': 'store_management',
            'Brand Management': 'brand_management',
            'Manage organization': 'manage_organization',
            Organization: 'manage_organization',
            'Customer Management': 'customer_management',
            Analytics: 'agent_analytics',
            Agents: 'agents',
            'Knowledge Base': 'knowledge_base',
            'Voice Library': 'voice_library',
            'Phone Numbers': 'phone_numbers',
            'Call Logs': 'call_logs',
            'WhatsApp Chats': 'whatsapp_chats',
            Campaign: 'campaign',
            Billing: 'billing',
            Integration: 'integrations',
          };

          const sectionKey = sectionKeyMap[item.title];
          if (!sectionKey) return true; // Show items that don't have a hideSections mapping

          // Don't hide if hideSections[sectionKey] is true (inverted logic)
          return !user.hideSections[sectionKey];
        }),
      }))
      .filter(category => category.items.length > 0); // Remove empty categories
  }, [menuItems, user?.hideSections]);

  // Redirect to first available section if agents section is hidden and user is on agents page
  useEffect(() => {
    if (user?.hideSections?.agents && location.pathname === routes.agents) {
      const fallback = getFirstAvailableRoute(user.hideSections);
      navigate(fallback.path, { replace: true });
    }
  }, [user?.hideSections, location.pathname, navigate]);

  const isPathActive = useCallback(
    (path: string) => {
      if (!path) return false;
      if (path === '/') {
        return location.pathname === '/';
      }
      return location.pathname === path || location.pathname.startsWith(path);
    },
    [location.pathname]
  );

  const handleItemClick = useCallback(
    (title: string, path?: string, event?: React.MouseEvent) => {
      // Find the item with subItems
      const itemWithSubs = filteredMenuItems
        .flatMap(category => category.items)
        .find(item => item.title === title);

      if (isCollapsed && itemWithSubs?.subItems && event) {
        // Show overlay menu for items with children when collapsed
        const rect = event.currentTarget.getBoundingClientRect();
        setOverlayMenu({
          isOpen: true,
          item: itemWithSubs,
          position: {
            top: rect.top,
            left: rect.right + 8, // 8px gap from sidebar
          },
        });
        return;
      }

      // Handle normal click behavior
      if (
        !isCollapsed &&
        (title === 'Agents' ||
          title === 'Knowledge Base' ||
          title === 'Analytics')
      ) {
        setSelectedMenus(prev => {
          const newSet = new Set(prev);
          if (newSet.has(title)) {
            newSet.delete(title);
          } else {
            newSet.add(title);
          }
          return newSet;
        });
      }

      if (path) {
        navigate(path);
      }
    },
    [navigate, isCollapsed, filteredMenuItems]
  );

  const handleSubItemClick = useCallback(
    (subItem: any, menuTitle: string, e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      setTimeout(() => {
        setSelectedMenus(prev => {
          const newSet = new Set(prev);
          if (!newSet.has(menuTitle)) {
            newSet.add(menuTitle);
          }
          return newSet;
        });
      }, 0);

      if (
        menuTitle === 'Agents' &&
        (subItem === 'No Agents' || subItem === 'Loading...')
      ) {
        return;
      }

      if (menuTitle === 'Agents') {
        const selectedAgent = agents?.agents?.find(
          (agent: { agentName: string }) => agent.agentName === subItem
        );
        if (selectedAgent) {
          setSelectedAssistant(selectedAgent as Agent);
          setSelectedAssistantId(selectedAgent._id as string);
          const formattedName = selectedAgent?.agentName
            ?.toLowerCase()
            .replace(/\s+/g, '-');
          navigate(`/agent/${encodeURIComponent(formattedName)}`);
        }
      } else if (menuTitle === 'Knowledge Base') {
        setSelectedAssistant(null);
        navigate(subItem.path);
      } else if (menuTitle === 'Analytics') {
        navigate(subItem.path);
      }
    },
    [agents, navigate, setSelectedAssistant, setSelectedAssistantId]
  );

  const handleOverlaySubItemClick = useCallback(
    (subItem: any, menuTitle: string) => {
      setOverlayMenu({
        isOpen: false,
        item: null,
        position: { top: 0, left: 0 },
      });

      if (
        menuTitle === 'Agents' &&
        (subItem === 'No Agents' || subItem === 'Loading...')
      ) {
        return;
      }

      if (menuTitle === 'Agents') {
        const selectedAgent = agents?.agents?.find(
          (agent: { agentName: string }) => agent.agentName === subItem
        );
        if (selectedAgent) {
          setSelectedAssistant(selectedAgent as Agent);
          setSelectedAssistantId(selectedAgent._id as string);
          const formattedName = selectedAgent?.agentName
            ?.toLowerCase()
            .replace(/\s+/g, '-');
          navigate(`/agent/${encodeURIComponent(formattedName)}`);
        }
      } else if (menuTitle === 'Knowledge Base') {
        setSelectedAssistant(null);
        navigate(subItem.path);
      } else if (menuTitle === 'Analytics') {
        navigate(subItem.path);
      }
    },
    [agents, navigate, setSelectedAssistant, setSelectedAssistantId]
  );

  const handleCloseOverlay = useCallback(() => {
    setOverlayMenu({
      isOpen: false,
      item: null,
      position: { top: 0, left: 0 },
    });
  }, []);

  useEffect(() => {
    const currentPath = location.pathname;

    if (currentPath.includes('/agent/')) {
      setSelectedMenus(prev => {
        const newSet = new Set(prev);
        newSet.add('Agents');
        return newSet;
      });

      const agentNameFromUrl = decodeURIComponent(
        currentPath.split('/agent/')[1]
      ).replace(/-/g, ' ');

      const matchingAgent = agents?.agents?.find(
        (agent: { agentName: string }) =>
          agent.agentName.toLowerCase() === agentNameFromUrl.toLowerCase()
      );

      if (matchingAgent) {
        setSelectedAssistant(matchingAgent as Agent);
        setSelectedAssistantId(matchingAgent._id as string);
      }
    } else {
      setSelectedAssistant(null);
      setSelectedAssistantId(null);
    }

    if (currentPath.includes('/knowledge-base/')) {
      setSelectedMenus(prev => {
        const newSet = new Set(prev);
        newSet.add('Knowledge Base');
        return newSet;
      });
    }

    if (currentPath.includes('/analytics/')) {
      setSelectedMenus(prev => {
        const newSet = new Set(prev);
        newSet.add('Analytics');
        return newSet;
      });
    }
  }, [
    location.pathname,
    agents?.agents,
    setSelectedAssistant,
    setSelectedAssistantId,
  ]);

  const shouldShowActiveBackground = useCallback(
    (item: any) => {
      if (
        item.title === 'Knowledge Base' &&
        location.pathname.includes('/knowledge-base/')
      ) {
        return true;
      }

      if (item.title === 'Agents' && location.pathname.includes('/agent/')) {
        return true;
      }

      if (
        item.title === 'Analytics' &&
        location.pathname.includes('/analytics/')
      ) {
        return true;
      }

      return isPathActive(item.path || '');
    },
    [isPathActive, location.pathname]
  );

  // Branding helper functions
  const renderBrandLogo = useCallback(
    (logoUrl?: string, size = 48) => {
      const imageUrl = logoUrl || logo;
      // Don't add cache busting for base64 images (previews)
      const isBase64 = logoUrl?.startsWith('data:');
      const cacheBustingUrl =
        logoUrl && !isBase64
          ? `${logoUrl}?t=${brandingConfig.logoMetadata?.updatedAt || Date.now()}`
          : imageUrl;

      return (
        <Avatar
          key={
            isBase64 ? `base64-${logoUrl?.substring(0, 50)}` : cacheBustingUrl
          }
          src={cacheBustingUrl}
          alt={logoUrl ? brandingConfig.title : 'QuibbleAI'}
          variant="rounded"
          sx={{
            width: size,
            height: size,
            borderRadius: 2,
            backgroundColor: 'transparent',
          }}
          imgProps={{
            style: {
              objectFit: 'contain',
            },
          }}
        />
      );
    },
    [brandingConfig.title, brandingConfig.logoMetadata?.updatedAt]
  );

  const handleBrandingClick = useCallback(() => {
    if (!isCollapsed) {
      setTempBranding(brandingConfig);
      setEditDialogOpen(true);
    }
  }, [isCollapsed, brandingConfig]);

  const handleLogoUpload = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setTempBranding(prev => ({
            ...prev,
            logo: reader.result as string,
          }));
        };
        reader.readAsDataURL(file);
        // Clear the input to allow re-uploading the same file
        event.target.value = '';
      }
    },
    []
  );

  const handleSaveBranding = useCallback(async () => {
    if (!user?._id) {
      showAlert('User not found', 'error');
      return;
    }

    try {
      const isNewLogo =
        tempBranding.logo && tempBranding.logo.startsWith('data:');
      const hasExistingLogo =
        brandingConfig.logoId ||
        brandingConfig.logoMetadata?._id ||
        tempBranding.logoMetadata?._id;
      const logoId =
        brandingConfig.logoId ||
        brandingConfig.logoMetadata?._id ||
        tempBranding.logoMetadata?._id;

      // Case 1: New logo upload (base64)
      if (isNewLogo && tempBranding.logo) {
        // Convert base64 to File
        const response = await fetch(tempBranding.logo);
        const blob = await response.blob();
        const file = new File([blob], 'logo.png', { type: blob.type });

        const formData = new FormData();
        formData.append('logo', file);
        formData.append('title', tempBranding.title);
        if (tempBranding.description) {
          formData.append('subtitle', tempBranding.description);
        }
        formData.append('userId', user._id);

        // If replacing existing logo
        if (hasExistingLogo && logoId) {
          await replaceLogoFileAsync({
            id: logoId,
            formData,
          });
        } else {
          await uploadLogoAsync(formData);
        }

        showAlert('Logo uploaded successfully', 'success');
      }
      // Case 2: Update metadata only (no new logo)
      else if (hasExistingLogo && logoId) {
        await updateLogoMetadataAsync({
          id: logoId,
          data: {
            userId: user._id,
            title: tempBranding.title,
            subtitle: tempBranding.description,
          },
        });
        showAlert('Branding updated successfully', 'success');
      }
      // Case 3: No logo, just update local state
      else {
        dispatch(updateBranding(tempBranding));
        showAlert('Branding updated successfully', 'success');
      }

      // Small delay to ensure Redux state is updated before closing
      await new Promise(resolve => setTimeout(resolve, 100));
      setEditDialogOpen(false);
    } catch (error: any) {
      console.error('Error saving branding:', error);
      showAlert(
        error?.response?.data?.message || 'Failed to save branding',
        'error'
      );
    }
  }, [
    dispatch,
    tempBranding,
    user,
    brandingConfig,
    uploadLogoAsync,
    updateLogoMetadataAsync,
    replaceLogoFileAsync,
    showAlert,
  ]);

  const handleCancelBranding = useCallback(() => {
    setTempBranding(brandingConfig);
    setEditDialogOpen(false);
  }, [brandingConfig]);

  const handleRemoveLogo = useCallback(async () => {
    try {
      // If there's an uploaded logo, delete it from the server
      const logoId = brandingConfig.logoId || brandingConfig.logoMetadata?._id;
      if (logoId && user?._id) {
        await deleteLogoAsync({
          id: logoId,
          userId: user._id,
        });
        showAlert('Logo removed successfully', 'success');
      }

      setTempBranding(prev => ({
        ...prev,
        logo: undefined,
        logoId: undefined,
        logoMetadata: undefined,
      }));
    } catch (error: any) {
      console.error('Error removing logo:', error);
      showAlert(
        error?.response?.data?.message || 'Failed to remove logo',
        'error'
      );
    }
  }, [
    brandingConfig.logoId,
    brandingConfig.logoMetadata,
    user,
    deleteLogoAsync,
    showAlert,
  ]);

  // Toggle collapse function
  const toggleCollapse = useCallback(() => {
    const newCollapsedState = !isCollapsed;
    if (collapsedProp === undefined) {
      setInternalCollapsed(newCollapsedState);
    }
    if (onCollapsedChange) {
      onCollapsedChange(newCollapsedState);
    }
    // If collapsing, close all expanded menus
    if (!isCollapsed) {
      setSelectedMenus(new Set());
    }
  }, [isCollapsed, collapsedProp, onCollapsedChange]);

  return (
    <>
      <Drawer
        variant="permanent"
        sx={{
          width: currentDrawerWidth,
          height: '100vh',
          flexShrink: 0,
          transition: 'width 0.3s ease-in-out',
          '& .MuiDrawer-paper': {
            width: currentDrawerWidth,
            transition: 'width 0.3s ease-in-out',
            overflow: 'hidden',
            backgroundColor: '#FFFFFF',
            position: 'relative',
            borderRight: `1px solid ${theme.palette.lightGray.main}`,
          },
        }}
      >
        {/* Branding Section */}
        <Box
          onClick={handleBrandingClick}
          sx={{
            paddingX: isCollapsed ? '8px' : '19px',
            paddingY: '16px',
            borderBottom: `1px solid ${theme.palette.lightGray.main}`,
            cursor: isCollapsed ? 'default' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: isCollapsed ? 'center' : 'flex-start',
            gap: 1.5,
            position: 'relative',
            transition: 'background-color 0.2s',
            '&:hover': !isCollapsed
              ? {
                  backgroundColor: theme.palette.custom.sidebarIconBg,
                  '& .action-icons': {
                    opacity: 1,
                  },
                }
              : {},
          }}
        >
          {!isCollapsed && (
            <Box
              className="action-icons"
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                opacity: 0,
                transition: 'opacity 0.2s',
                display: 'flex',
                gap: 0.5,
              }}
            >
              <IconButton
                size="small"
                onClick={e => {
                  e.stopPropagation();
                  toggleCollapse();
                }}
                sx={{
                  backgroundColor: '#FFFFFF',
                  '&:hover': {
                    backgroundColor: theme.palette.custom.sidebarActiveBg,
                  },
                }}
              >
                <CaretLeftIcon size={16} weight="regular" />
              </IconButton>
              <IconButton
                size="small"
                sx={{
                  backgroundColor: '#FFFFFF',
                  '&:hover': {
                    backgroundColor: theme.palette.custom.sidebarActiveBg,
                  },
                }}
              >
                <PencilSimpleIcon size={16} weight="regular" />
              </IconButton>
            </Box>
          )}

          {isCollapsed ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Tooltip
                title={
                  <>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {brandingConfig.title}
                    </Typography>
                    {brandingConfig.description && (
                      <Typography variant="caption">
                        {brandingConfig.description}
                      </Typography>
                    )}
                  </>
                }
                placement="right"
              >
                <Box>{renderBrandLogo(brandingConfig.logo, 40)}</Box>
              </Tooltip>
              <IconButton
                size="small"
                onClick={e => {
                  e.stopPropagation();
                  toggleCollapse();
                }}
                sx={{
                  color: theme.palette.lightGray.text,
                  '&:hover': {
                    backgroundColor: theme.palette.custom.sidebarIconBg,
                  },
                }}
              >
                <CaretRightIcon size={16} weight="regular" />
              </IconButton>
            </Box>
          ) : (
            <>
              {renderBrandLogo(brandingConfig.logo, 48)}
              <Box sx={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    fontSize: '16px',
                    lineHeight: 1.2,
                    color: theme.palette.custom.textBlue,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={brandingConfig.title}
                >
                  {brandingConfig.title}
                </Typography>
                {brandingConfig.description && (
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '12px',
                      color: theme.palette.lightGray.text,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    title={brandingConfig.description}
                  >
                    {brandingConfig.description}
                  </Typography>
                )}
              </Box>
            </>
          )}
        </Box>

        <List
          sx={{
            paddingX: isCollapsed ? '8px' : '19px',
            paddingTop: '16px',
            gap: '10px',
            display: 'flex',
            flexDirection: 'column',
            height: 'calc(100% - 66px)',
            position: 'relative',
          }}
        >
          {!isCollapsed && (
            <div style={{ width: '100%' }}>
              <AgentAndBusinessButton />
            </div>
          )}

          <Box
            sx={{
              overflow: 'auto',
              flex: 1,
              mb: '80px',
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': {
                display: 'none',
              },
            }}
          >
            <div
              ref={sidebarRef}
              style={{
                overflow: 'auto',
                height: 'calc(100% - 1px)',
                position: 'relative',
              }}
              className="hide-scrollbar"
            >
              {filteredMenuItems.map((category, categoryIndex) => (
                <React.Fragment key={`category-${categoryIndex}`}>
                  {!isCollapsed && (
                    <>
                      <Box sx={{ my: 2 }}>
                        {categoryIndex > 0 && (
                          <Divider
                            sx={{ borderColor: theme.palette.lightGray.main }}
                          />
                        )}
                      </Box>

                      <Typography
                        sx={{
                          fontSize: '12px',
                          color: '#9CA3AF',
                          fontWeight: 500,
                          mt: categoryIndex > 0 ? 2 : 0,
                          mb: 1,
                          pl: 1,
                        }}
                      >
                        {category.category}
                      </Typography>
                    </>
                  )}

                  {category.items.map(item => (
                    <React.Fragment key={item.title}>
                      {isCollapsed ? (
                        <Tooltip
                          title={
                            item.subItems
                              ? `${item.title} (Click to see options)`
                              : item.title
                          }
                          placement="right"
                        >
                          <ListItem
                            onClick={e =>
                              handleItemClick(item.title, item?.path || '', e)
                            }
                            tabIndex={-1}
                            sx={{
                              outline: 'none',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              paddingX: '8px',
                              paddingY: '6px',
                              marginY: '2px',
                              cursor: 'pointer',
                              '&:hover': {
                                backgroundColor:
                                  theme.palette.custom.sidebarIconBg,
                              },
                              backgroundColor: shouldShowActiveBackground(item)
                                ? theme.palette.custom.sidebarActiveBg
                                : 'transparent',
                              borderRadius: '6px',
                              minHeight: '40px',
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                fontWeight: '500',
                                minWidth: 'unset',
                                width: '24px',
                                height: '24px',
                                backgroundColor: shouldShowActiveBackground(
                                  item
                                )
                                  ? '#FFFFFF'
                                  : theme.palette.custom.sidebarIconBg,
                                borderRadius: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: shouldShowActiveBackground(item)
                                  ? theme.palette.custom.textBlue
                                  : theme.palette.lightGray.text,
                                position: 'relative',
                              }}
                            >
                              {item.icon}
                              {/* Add small indicator for items with children */}
                              {item.subItems && (
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    bottom: '-2px',
                                    right: '-2px',
                                    width: '6px',
                                    height: '6px',
                                    backgroundColor:
                                      theme.palette.custom.textBlue,
                                    borderRadius: '50%',
                                    border: '1px solid white',
                                  }}
                                />
                              )}
                            </ListItemIcon>
                          </ListItem>
                        </Tooltip>
                      ) : (
                        <ListItem
                          onClick={e =>
                            handleItemClick(item.title, item?.path || '', e)
                          }
                          tabIndex={-1}
                          sx={{
                            outline: 'none',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            gap: '8px',
                            paddingX: '8px',
                            paddingY: '6px',
                            marginY: '2px',
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor:
                                theme.palette.custom.sidebarIconBg,
                            },
                            backgroundColor: shouldShowActiveBackground(item)
                              ? theme.palette.custom.sidebarActiveBg
                              : 'transparent',
                            borderRadius: '6px',
                            minHeight: '40px',
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              fontWeight: '500',
                              minWidth: 'unset',
                              width: '24px',
                              height: '24px',
                              backgroundColor: shouldShowActiveBackground(item)
                                ? '#FFFFFF'
                                : theme.palette.custom.sidebarIconBg,
                              borderRadius: '4px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: shouldShowActiveBackground(item)
                                ? theme.palette.custom.textBlue
                                : theme.palette.lightGray.text,
                            }}
                          >
                            {item.icon}
                          </ListItemIcon>
                          <ListItemText
                            primary={item.title}
                            sx={{
                              '& .MuiTypography-root': {
                                fontSize: '14px',
                                lineHeight: '20px',
                                width: '100%',
                                color: shouldShowActiveBackground(item)
                                  ? theme.palette.custom.textBlue
                                  : theme.palette.lightGray.text,
                                fontWeight: shouldShowActiveBackground(item)
                                  ? 500
                                  : 400,
                              },
                            }}
                          />
                          {item?.subItems && (
                            <CaretDownIcon size={16} color="#6B6F80" />
                          )}
                        </ListItem>
                      )}

                      {!isCollapsed && (
                        <Collapse
                          in={selectedMenus.has(item.title)}
                          timeout="auto"
                          unmountOnExit
                        >
                          <List component="div" disablePadding>
                            {item?.subItems?.map(
                              (subItem: any, index: number) => (
                                <ListItem
                                  key={index}
                                  component="div"
                                  onClick={e =>
                                    handleSubItemClick(subItem, item.title, e)
                                  }
                                  sx={{
                                    pl: 4,
                                    position: 'relative',
                                    cursor: 'pointer',
                                    '&::before': {
                                      content: '""',
                                      position: 'absolute',
                                      left: '8px',
                                      top: index === 0 ? '-25%' : '0',
                                      bottom:
                                        index === item.subItems.length - 1
                                          ? '45%'
                                          : '0',
                                      width: '1px',
                                      backgroundColor:
                                        theme.palette.lightGray.main,
                                    },
                                    '&::after': {
                                      content: '""',
                                      position: 'absolute',
                                      left: '8px',
                                      top: '51%',
                                      width: '12px',
                                      height: '2px',
                                      backgroundColor:
                                        theme.palette.lightGray.main,
                                    },
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: '100%',
                                      ml: '-18px',
                                      pl: '10px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '8px',
                                    }}
                                  >
                                    {isLoading ? (
                                      <Skeleton
                                        variant="text"
                                        width="80%"
                                        height={20}
                                        sx={{
                                          bgcolor: theme.palette.lightGray.main,
                                        }}
                                      />
                                    ) : (
                                      <Box
                                        sx={{
                                          display: 'inline-block',
                                          backgroundColor:
                                            (item.title === 'Agents' &&
                                              selectedAssistant?.agentName ===
                                                subItem) ||
                                            location.pathname ===
                                              (subItem.path || '')
                                              ? theme.palette.custom
                                                  .sidebarActiveBg
                                              : 'transparent',
                                          borderRadius: '8px',
                                          px: 1,
                                          py: 0.5,
                                        }}
                                      >
                                        <ListItemText
                                          primary={
                                            typeof subItem === 'string'
                                              ? subItem
                                              : subItem.title
                                          }
                                          sx={{
                                            '& .MuiTypography-root': {
                                              fontSize: '14px',
                                              fontWeight: '400',
                                              color: `${theme.palette.lightGray.text}`,
                                              whiteSpace: 'nowrap',
                                              overflow: 'hidden',
                                              textOverflow: 'ellipsis',
                                              margin: 0,
                                            },
                                          }}
                                        />
                                      </Box>
                                    )}
                                  </Box>
                                </ListItem>
                              )
                            )}
                          </List>
                        </Collapse>
                      )}
                    </React.Fragment>
                  ))}
                </React.Fragment>
              ))}
            </div>
          </Box>

          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              backgroundColor: '#FFFFFF',
              paddingX: isCollapsed ? '8px' : '19px',
              paddingY: '8px',
            }}
          >
            <Box sx={{ width: '100%' }}>
              <UserProfileOptionMenu isopen={!isCollapsed} />
            </Box>
          </Box>
        </List>
      </Drawer>

      {/* Overlay Menu for Collapsed State */}
      {overlayMenu.isOpen && isCollapsed && (
        <ClickAwayListener onClickAway={handleCloseOverlay}>
          <Paper
            elevation={8}
            sx={{
              position: 'fixed',
              top: overlayMenu.position.top,
              left: overlayMenu.position.left,
              zIndex: 1400,
              backgroundColor: '#FFFFFF',
              borderRadius: '8px',
              border: `1px solid ${theme.palette.lightGray.main}`,
              minWidth: '200px',
              maxWidth: '280px',
            }}
          >
            <Box sx={{ p: 1 }}>
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: theme.palette.lightGray.text,
                  px: 1,
                  py: 0.5,
                  borderBottom: `1px solid ${theme.palette.lightGray.main}`,
                  mb: 1,
                }}
              >
                {overlayMenu.item.title}
              </Typography>

              <List component="div" disablePadding>
                {overlayMenu.item.title === 'Agents' ? (
                  <>
                    {isLoading ? (
                      <ListItem sx={{ py: 0.5 }}>
                        <Skeleton
                          variant="text"
                          width="100%"
                          height={20}
                          sx={{ bgcolor: theme.palette.lightGray.main }}
                        />
                      </ListItem>
                    ) : agents?.agents?.length > 0 ? (
                      agents.agents.map((agent: any, index: number) => (
                        <ListItem
                          key={index}
                          onClick={() =>
                            handleOverlaySubItemClick(agent.agentName, 'Agents')
                          }
                          sx={{
                            cursor: 'pointer',
                            borderRadius: '6px',
                            mx: 0.5,
                            my: 0.25,
                            py: 0.5,
                            px: 1,
                            '&:hover': {
                              backgroundColor:
                                theme.palette.custom.sidebarIconBg,
                            },
                            backgroundColor:
                              selectedAssistant?.agentName === agent.agentName
                                ? theme.palette.custom.sidebarActiveBg
                                : 'transparent',
                          }}
                        >
                          <ListItemText
                            primary={agent.agentName}
                            sx={{
                              '& .MuiTypography-root': {
                                fontSize: '14px',
                                fontWeight: '400',
                                color: theme.palette.lightGray.text,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              },
                            }}
                          />
                        </ListItem>
                      ))
                    ) : (
                      <ListItem sx={{ py: 0.5 }}>
                        <ListItemText
                          primary="No Agents"
                          sx={{
                            '& .MuiTypography-root': {
                              fontSize: '14px',
                              fontWeight: '400',
                              color: '#9CA3AF',
                              textAlign: 'center',
                            },
                          }}
                        />
                      </ListItem>
                    )}
                  </>
                ) : overlayMenu.item.subItems ? (
                  overlayMenu.item.subItems.map(
                    (subItem: any, index: number) => (
                      <ListItem
                        key={index}
                        onClick={() =>
                          handleOverlaySubItemClick(
                            subItem,
                            overlayMenu.item.title
                          )
                        }
                        sx={{
                          cursor: 'pointer',
                          borderRadius: '6px',
                          mx: 0.5,
                          my: 0.25,
                          py: 0.5,
                          px: 1,
                          '&:hover': {
                            backgroundColor: theme.palette.custom.sidebarIconBg,
                          },
                          backgroundColor:
                            location.pathname === (subItem.path || '')
                              ? theme.palette.custom.sidebarActiveBg
                              : 'transparent',
                        }}
                      >
                        <ListItemText
                          primary={
                            typeof subItem === 'string'
                              ? subItem
                              : subItem.title
                          }
                          sx={{
                            '& .MuiTypography-root': {
                              fontSize: '14px',
                              fontWeight: '400',
                              color: theme.palette.lightGray.text,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            },
                          }}
                        />
                      </ListItem>
                    )
                  )
                ) : null}
              </List>
            </Box>
          </Paper>
        </ClickAwayListener>
      )}

      {/* Edit Branding Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={handleCancelBranding}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Branding</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Logo Upload Section */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Logo
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {renderBrandLogo(tempBranding.logo, 56)}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleLogoUpload}
                    accept="image/*"
                    style={{ display: 'none' }}
                  />
                  <Button
                    variant="outlined"
                    startIcon={<UploadIcon size={20} weight="regular" />}
                    onClick={() => fileInputRef.current?.click()}
                    size="small"
                  >
                    Upload Logo
                  </Button>
                  {tempBranding.logo && (
                    <Button
                      variant="text"
                      color="error"
                      onClick={handleRemoveLogo}
                      size="small"
                      disabled={isDeletingLogo}
                      startIcon={
                        isDeletingLogo ? (
                          <CircularProgress size={16} color="error" />
                        ) : null
                      }
                    >
                      {isDeletingLogo ? 'Removing...' : 'Remove Logo'}
                    </Button>
                  )}
                </Box>
              </Box>
            </Box>

            {/* Title Field */}
            <TextField
              label="Title"
              value={tempBranding.title}
              onChange={e =>
                setTempBranding(prev => ({ ...prev, title: e.target.value }))
              }
              fullWidth
              required
            />

            {/* Description Field */}
            <TextField
              label="Description"
              value={tempBranding.description || ''}
              onChange={e =>
                setTempBranding(prev => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              fullWidth
              placeholder="Optional"
              multiline
              rows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCancelBranding}
            disabled={isUploadingLogo || isUpdatingMetadata || isReplacingLogo}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveBranding}
            variant="contained"
            disabled={
              !tempBranding.title.trim() ||
              isUploadingLogo ||
              isUpdatingMetadata ||
              isReplacingLogo
            }
            startIcon={
              isUploadingLogo || isUpdatingMetadata || isReplacingLogo ? (
                <CircularProgress size={20} />
              ) : null
            }
          >
            {isUploadingLogo || isUpdatingMetadata || isReplacingLogo
              ? 'Saving...'
              : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default React.memo(DashboardSidebar);
